import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';
import {
  useStockTakeSessions,
  useStockTakeRecords,
  useGenerateStockTake,
  useBatchUpdateStockTakeRecords,
} from '@/hooks/usePSI';
import { IStockTakeRecord, IStockTakeSession } from '@/types/psi';

interface StockTakingDialogProps {
  onClose: () => void;
}

// Track edited physical quantities by record ID
interface EditedRecord {
  physical_qty: number;
  notes: string;
}

const StockTakingDialog: React.FC<StockTakingDialogProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Current date for defaults
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  // Track edited records
  const [editedRecords, setEditedRecords] = useState<Map<string, EditedRecord>>(new Map());

  // Queries and mutations
  const { data: sessions, isLoading: sessionsLoading } = useStockTakeSessions();
  const {
    data: stockTakeData,
    isLoading: recordsLoading,
    refetch: refetchRecords,
  } = useStockTakeRecords(selectedYear, selectedMonth);
  const generateMutation = useGenerateStockTake();
  const batchUpdateMutation = useBatchUpdateStockTakeRecords();

  // Check if session exists for selected month
  const existingSession = useMemo(() => {
    return sessions?.find(
      (s: IStockTakeSession) => s.year === selectedYear && s.month === selectedMonth
    );
  }, [sessions, selectedYear, selectedMonth]);

  // Get records (either from query or empty)
  const records = useMemo(() => {
    return stockTakeData?.items || [];
  }, [stockTakeData]);

  // Calculate progress
  const progress = useMemo(() => {
    const total = records.length;
    const counted = records.filter((r: IStockTakeRecord) => r.counted_at !== '').length;
    const percentage = total > 0 ? Math.round((counted / total) * 100) : 0;
    return { total, counted, remaining: total - counted, percentage };
  }, [records]);

  // Generate month options
  const months = [
    { value: 1, label: t('psi.months.january', 'January') },
    { value: 2, label: t('psi.months.february', 'February') },
    { value: 3, label: t('psi.months.march', 'March') },
    { value: 4, label: t('psi.months.april', 'April') },
    { value: 5, label: t('psi.months.may', 'May') },
    { value: 6, label: t('psi.months.june', 'June') },
    { value: 7, label: t('psi.months.july', 'July') },
    { value: 8, label: t('psi.months.august', 'August') },
    { value: 9, label: t('psi.months.september', 'September') },
    { value: 10, label: t('psi.months.october', 'October') },
    { value: 11, label: t('psi.months.november', 'November') },
    { value: 12, label: t('psi.months.december', 'December') },
  ];

  // Generate year options (current year +/- 2 years)
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  // Handle generate stock take
  const handleGenerate = useCallback(async () => {
    try {
      await generateMutation.mutateAsync({
        year: selectedYear,
        month: selectedMonth,
      });
      setEditedRecords(new Map());
      refetchRecords();
    } catch (error) {
      logger.error('Failed to generate stock take:', error);
    }
  }, [generateMutation, selectedYear, selectedMonth, refetchRecords]);

  // Handle physical qty change
  const handlePhysicalQtyChange = useCallback((recordId: string, value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) && value !== '') return;

    setEditedRecords((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(recordId) || { physical_qty: 0, notes: '' };
      newMap.set(recordId, { ...existing, physical_qty: numValue || 0 });
      return newMap;
    });
  }, []);

  // Handle notes change
  const handleNotesChange = useCallback((recordId: string, value: string) => {
    setEditedRecords((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(recordId) || { physical_qty: 0, notes: '' };
      newMap.set(recordId, { ...existing, notes: value });
      return newMap;
    });
  }, []);

  // Get display value for physical qty
  const getPhysicalQtyValue = useCallback(
    (record: IStockTakeRecord) => {
      const edited = editedRecords.get(record.id);
      if (edited !== undefined) {
        return edited.physical_qty;
      }
      return record.physical_qty;
    },
    [editedRecords]
  );

  // Get display value for notes
  const getNotesValue = useCallback(
    (record: IStockTakeRecord) => {
      const edited = editedRecords.get(record.id);
      if (edited !== undefined) {
        return edited.notes;
      }
      return record.notes;
    },
    [editedRecords]
  );

  // Calculate variance for display
  const getVariance = useCallback(
    (record: IStockTakeRecord) => {
      const physicalQty = getPhysicalQtyValue(record);
      return physicalQty - record.system_qty;
    },
    [getPhysicalQtyValue]
  );

  // Check if there are unsaved changes
  const hasChanges = editedRecords.size > 0;

  // Handle save all changes
  const handleSaveAll = useCallback(async () => {
    if (!hasChanges) return;

    const updates = Array.from(editedRecords.entries()).map(([id, edited]) => ({
      id,
      physical_qty: edited.physical_qty,
      counted_by: user?.name || 'Unknown',
      notes: edited.notes,
    }));

    try {
      await batchUpdateMutation.mutateAsync({
        year: selectedYear,
        month: selectedMonth,
        data: { updates },
      });
      setEditedRecords(new Map());
      refetchRecords();
    } catch (error) {
      logger.error('Failed to save stock take records:', error);
    }
  }, [
    hasChanges,
    editedRecords,
    user,
    batchUpdateMutation,
    selectedYear,
    selectedMonth,
    refetchRecords,
  ]);

  // Row background color based on variance
  const getRowClassName = useCallback(
    (record: IStockTakeRecord) => {
      const variance = getVariance(record);
      if (variance < 0) return 'bg-red-50 dark:bg-red-900/20';
      if (variance > 0) return 'bg-yellow-50 dark:bg-yellow-900/20';
      return '';
    },
    [getVariance]
  );

  const isLoading = sessionsLoading || recordsLoading;
  const isGenerating = generateMutation.isLoading;
  const isSaving = batchUpdateMutation.isLoading;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {t('psi.stockTaking.title', 'Stock Taking')}
              </h2>
              <p className="text-blue-100 text-sm">
                {t('psi.stockTaking.subtitle', 'Physical inventory count verification')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Month/Year Selector */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('psi.stockTaking.year', 'Year')}:
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading || isGenerating}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('psi.stockTaking.month', 'Month')}:
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading || isGenerating}
              >
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleGenerate}
              disabled={isLoading || isGenerating}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t('psi.stockTaking.generating', 'Generating...')}
                </>
              ) : existingSession ? (
                t('psi.stockTaking.regenerate', 'Regenerate Stock Take')
              ) : (
                t('psi.stockTaking.generate', 'Generate Stock Take')
              )}
            </button>

            {existingSession && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('psi.stockTaking.existingSession', 'Session exists: {{count}} items', {
                  count: existingSession.record_count,
                })}
              </span>
            )}
          </div>

          {/* Progress Summary */}
          {records.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('psi.stockTaking.progress', 'Progress')}: {progress.counted} /{' '}
                  {progress.total} {t('psi.stockTaking.items', 'items')}
                </span>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {progress.percentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                <div
                  className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
              {progress.remaining > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {t('psi.stockTaking.remaining', '{{count}} items remaining', {
                    count: progress.remaining,
                  })}
                </p>
              )}
            </div>
          )}

          {/* Stock Take Table */}
          {isLoading ? (
            <div className="text-center py-12">
              <svg
                className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">
                {t('psi.stockTaking.loading', 'Loading stock take data...')}
              </p>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('psi.stockTaking.noData', 'No Stock Take Data')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t(
                  'psi.stockTaking.noDataDescription',
                  'Generate a new stock take to start counting inventory for the selected month.'
                )}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('psi.stockTaking.partCode', 'Part Code')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('psi.stockTaking.specification', 'Specification')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('psi.stockTaking.warehouse', 'WH')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('psi.stockTaking.systemQty', 'System')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('psi.stockTaking.physicalQty', 'Physical')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('psi.stockTaking.variance', 'Var')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('psi.stockTaking.notes', 'Notes')}
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-8">
                      {/* Warning column */}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {records.map((record: IStockTakeRecord) => {
                    const variance = getVariance(record);
                    const hasVariance = variance !== 0;

                    return (
                      <tr key={record.id} className={getRowClassName(record)}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {record.part_code}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300 max-w-xs truncate">
                          {record.specification}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {record.warehouse}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white font-mono">
                          {record.system_qty}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <input
                            type="number"
                            value={getPhysicalQtyValue(record)}
                            onChange={(e) => handlePhysicalQtyChange(record.id, e.target.value)}
                            className="w-20 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                            min="0"
                          />
                        </td>
                        <td
                          className={`px-4 py-3 whitespace-nowrap text-sm text-right font-mono font-medium ${
                            variance < 0
                              ? 'text-red-600 dark:text-red-400'
                              : variance > 0
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-gray-500 dark:text-gray-300'
                          }`}
                        >
                          {variance > 0 ? '+' : ''}
                          {variance}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={getNotesValue(record)}
                            onChange={(e) => handleNotesChange(record.id, e.target.value)}
                            placeholder={t('psi.stockTaking.addNote', 'Add note...')}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-2 py-3 text-center">
                          {hasVariance && (
                            <svg
                              className={`w-5 h-5 ${
                                variance < 0 ? 'text-red-500' : 'text-yellow-500'
                              }`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-600">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {hasChanges && (
              <span className="text-orange-500">
                {t('psi.stockTaking.unsavedChanges', '{{count}} unsaved changes', {
                  count: editedRecords.size,
                })}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleSaveAll}
              disabled={!hasChanges || isSaving}
              className="px-4 py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t('psi.stockTaking.saving', 'Saving...')}
                </>
              ) : (
                t('psi.stockTaking.saveAll', 'Save All Changes')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockTakingDialog;
